import { tauriMode } from '@/shared/utils/import-meta'
import { getFullURL, getPathNameFromURL } from '@/shared/utils/url'

import { downloadFileTauri } from './tauri-download-file'

export type DownloadUrlItem = {
  /** Absolute URL or app path (`/files/...`). */
  url: string
  filename?: string
}

const DEFAULT_DELAY_MS = 400

const sleep = (ms: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, ms)
})

const toMediaPath = (url: string) => {
  if (!url) return url
  if (/^https?:/i.test(url)) return getPathNameFromURL(url)
  return url.startsWith('/') ? url : `/${url}`
}

const resolveFilename = (item: DownloadUrlItem, path: string) => {
  if (item.filename) return item.filename
  return path.split('/').filter(Boolean).at(-1) ?? 'download'
}

const triggerDownloadWeb = (item: DownloadUrlItem) => {
  const path = toMediaPath(item.url)
  const link = document.createElement('a')
  link.href = getFullURL(path)
  link.download = resolveFilename(item, path)
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

const triggerDownloadTauri = async (item: DownloadUrlItem) => {
  const path = toMediaPath(item.url)
  await downloadFileTauri({
    filename: resolveFilename(item, path),
    filecookie: path,
  })
}

/**
 * Downloads files one by one with a delay so browsers do not block multiple downloads.
 * Tauri: authenticated fetch + save dialog (same as download()).
 */
export const downloadUrlsSequentially = async (
  items: DownloadUrlItem[],
  delayMs = DEFAULT_DELAY_MS,
): Promise<void> => {
  if (!items.length) {
    return
  }

  const isTauri = tauriMode()

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (!item?.url) {
      continue
    }

    if (isTauri) {
      await triggerDownloadTauri(item)
    } else {
      triggerDownloadWeb(item)
    }

    if (i < items.length - 1 && delayMs > 0) {
      await sleep(delayMs)
    }
  }
}
